import { imgBaseUrl } from "../../api/api";

/**
 * Consolidated result table — ONE design used for both on-screen preview and print.
 *
 *  - School header (logo, name, address) + test / batch / session details
 *  - Summary strip (students, appeared, absent, highest %, class average %)
 *  - Subject columns = union of every student's subjects (students can have
 *    different subjects). A student only gets marks for the subjects they
 *    were examined in, other cells show "N/A".
 *  - If students have different total marks, an extra "Out of" column is shown
 *  - Ranks 1-3 highlighted (when the Rank column is shown)
 *  - Order: ranked students -> marks not entered -> absent
 *  - Class Average / Highest rows at the end
 *  - A4 landscape print, header row repeats on every page, rows never split
 *  - Optional: Rank column and each signature can be hidden via `show`,
 *    and the principal's saved signature image can be printed above the line.
 *
 * Student status (from API):
 *   absent    -> is_absent === "1"  (or every subject marked "A")
 *   pending   -> not absent, but obtained_total is null (marks not entered)
 *   appeared  -> everything else
 *
 * Exports:
 *   getProgressStatus(student)      -> "appeared" | "absent" | "pending"
 *   getProgressPercent(student)     -> number
 *   getProgressSubjects(students)   -> [{ key, name, max }] union of subjects
 *   sortProgressStudents(students)  -> sorted copy
 *   buildProgressTableHtml(opts)    -> html string (root .ptp element included)
 *   getProgressTableCss()           -> css string (scoped under .ptp)
 *   printProgressTable(opts)        -> opens print window
 *
 * opts = { students, testTitle, batchTitle, sessionName, show }
 *
 * show = {
 *   rank, classTeacher, examIncharge, principal, signatureImg
 * }   (all booleans; every key defaults to true except signatureImg = false)
 */

const DEFAULT_TABLE_SHOW = {
  rank: true,
  classTeacher: true,
  examIncharge: true,
  principal: true,
  signatureImg: false,
};

const esc = (val) =>
  String(val ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );

const getSetting = () => {
  try {
    return JSON.parse(localStorage.getItem("setting") ?? "{}") || {};
  } catch {
    return {};
  }
};

const isNum = (v) =>
  v !== "" && v !== null && v !== undefined && !isNaN(Number(v));

const fmt = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
const fmtDob = (v) => {
  if (!v) return "—";
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(v);
};

const subKey = (sub) => String(sub?.id ?? sub?.name);

/* ------------------------------------------------------------- helpers */

export const getProgressStatus = (student) => {
  if (String(student?.is_absent) === "1") return "absent";

  const subs = student?.subject_details || [];
  if (subs.length > 0 && subs.every((s) => s?.obtained === "A")) {
    return "absent";
  }

  const t = student?.obtained_total;
  if (t === null || t === undefined || t === "") return "pending";

  return "appeared";
};

export const getProgressPercent = (student) => {
  const total = Number(student?.total_marks || 0);
  if (!total) return 0;
  return (Number(student?.obtained_total || 0) / total) * 100;
};

/**
 * Union of all subjects across all students (first-seen order).
 * Students can have different subjects, so columns must not come from
 * a single student.
 */
export const getProgressSubjects = (students = []) => {
  const seen = new Map();

  students.forEach((s) => {
    (s?.subject_details || []).forEach((sub) => {
      const key = subKey(sub);
      if (!seen.has(key)) {
        seen.set(key, { key, name: sub?.name, max: sub?.subject_marks });
      }
    });
  });

  return [...seen.values()];
};

const findSubject = (student, key) =>
  (student?.subject_details || []).find((x) => subKey(x) === key);

const GROUP = { appeared: 0, pending: 1, absent: 2 };

export const sortProgressStudents = (students = []) =>
  [...students].sort((a, b) => {
    const ga = GROUP[getProgressStatus(a)];
    const gb = GROUP[getProgressStatus(b)];
    if (ga !== gb) return ga - gb;

    const ra = Number(a?.rank || 9999);
    const rb = Number(b?.rank || 9999);
    if (ra !== rb) return ra - rb;

    return String(a?.student_details?.name || "").localeCompare(
      String(b?.student_details?.name || ""),
      undefined,
      { sensitivity: "base" },
    );
  });

/* ---------------------------------------------------------------- HTML */

export const buildProgressTableHtml = ({
  students = [],
  testTitle = "",
  batchTitle = "",
  sessionName = "",
  show = DEFAULT_TABLE_SHOW,
}) => {
  const sh = { ...DEFAULT_TABLE_SHOW, ...show };

  const setting = getSetting();
  const logo = setting?.logo_img ? imgBaseUrl + setting.logo_img : "";
  const signatureUrl = setting?.signature ? imgBaseUrl + setting.signature : "";
  const address = [setting?.village, setting?.district, setting?.state]
    .filter(Boolean)
    .join(", ");

  const signs = [
    sh.classTeacher && { label: "Class Teacher" },
    sh.examIncharge && { label: "Examination In-charge" },
    sh.principal && {
      label: "Principal",
      img: sh.signatureImg ? signatureUrl : "",
    },
  ].filter(Boolean);

  const subjects = getProgressSubjects(students);

  // do all students share the same total marks?
  const totalSet = new Set(students.map((s) => String(s?.total_marks ?? "")));
  const sameTotal = totalSet.size <= 1;
  const maxTotal = students[0]?.total_marks ?? "";

  // font size class by number of columns
  const fs =
    subjects.length <= 5
      ? "fs-lg"
      : subjects.length <= 8
        ? "fs-md"
        : subjects.length <= 11
          ? "fs-sm"
          : "fs-xs";

  /* ---- stats ---- */
  const appeared = students.filter((s) => getProgressStatus(s) === "appeared");
  const absentCount = students.filter(
    (s) => getProgressStatus(s) === "absent",
  ).length;
  const pendingCount = students.filter(
    (s) => getProgressStatus(s) === "pending",
  ).length;

  const highest = appeared.length
    ? Math.max(...appeared.map(getProgressPercent))
    : 0;
  const average = appeared.length
    ? appeared.reduce((sum, s) => sum + getProgressPercent(s), 0) /
      appeared.length
    : 0;

  /* ---- per-subject average / highest (only students who have the subject) ---- */
  const subjectStats = subjects.map((sub) => {
    const vals = appeared
      .map((s) => findSubject(s, sub.key)?.obtained)
      .filter(isNum)
      .map(Number);
    return {
      avg: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null,
      max: vals.length ? Math.max(...vals) : null,
    };
  });

  const totals = appeared.map((s) => Number(s?.obtained_total || 0));
  const totalAvg = totals.length
    ? totals.reduce((a, b) => a + b, 0) / totals.length
    : null;
  const totalMax = totals.length ? Math.max(...totals) : null;

  /* ---- body rows ---- */
  const body = students
    .map((s, idx) => {
      const d = s?.student_details || {};
      const status = getProgressStatus(s);
      const isAppeared = status === "appeared";
      const rank = Number(s?.rank);
      const rankCls =
        isAppeared && rank >= 1 && rank <= 3 ? ` top r${rank}` : "";

      const cells = subjects
        .map((sub) => {
          const own = findSubject(s, sub.key);

          // student has no exam in this subject
          if (!own) return `<td class="na">N/A</td>`;

          if (status === "absent" || own?.obtained === "A") {
            return `<td><span class="ab">AB</span></td>`;
          }
          if (!isNum(own?.obtained)) return `<td class="dash">-</td>`;
          return `<td>${esc(own.obtained)}</td>`;
        })
        .join("");

      return `
      <tr class="${status === "appeared" ? "" : status}">
        <td class="sr">${idx + 1}</td>
        <td class="l name">${esc(d?.name || "—")}</td>
        <td class="l">${esc(d?.father_name || "—")}</td>
        <td class="l">${esc(d?.mother_name || "—")}</td>
        <td>${esc(fmtDob(d?.dob))}</td>
        <td>${esc(d?.registration_no || "—")}</td>
        ${cells}
        <td class="tot">${isAppeared ? esc(s?.obtained_total) : status === "absent" ? `<span class="ab">AB</span>` : "-"}</td>
        ${sameTotal ? "" : `<td>${esc(s?.total_marks ?? "-")}</td>`}
        <td>${isAppeared ? fmt(getProgressPercent(s)) + "%" : "-"}</td>
        ${
          sh.rank
            ? `<td>${
                isAppeared && s?.rank
                  ? `<span class="rk${rankCls}">${esc(s.rank)}</span>`
                  : "-"
              }</td>`
            : ""
        }
      </tr>`;
    })
    .join("");

  const summaryRow = (label, key, totalVal, pctVal) => `
      <tr class="sum">
        <td colspan="6" class="l">${label}</td>
        ${subjectStats
          .map((st) => `<td>${st[key] === null ? "-" : fmt(st[key])}</td>`)
          .join("")}
        <td class="tot">${sameTotal && totalVal !== null ? fmt(totalVal) : "-"}</td>
        ${sameTotal ? "" : "<td></td>"}
        <td>${appeared.length ? fmt(pctVal) + "%" : "-"}</td>
        ${sh.rank ? "<td></td>" : ""}
      </tr>`;

  /* ---- summary strip ---- */
  const metaCells = [
    ["Batch", esc(batchTitle || "—")],
    ["Session", esc(sessionName || "—")],
    ["Students", students.length],
    ["Appeared", appeared.length],
    ["Absent", absentCount],
    ...(pendingCount ? [["Not Entered", pendingCount]] : []),
    ["Highest %", fmt(highest) + "%"],
    ["Class Avg %", fmt(average) + "%"],
  ];

  return `
  <div class="ptp ${fs}">
    <div class="rep-head">
      ${logo ? `<img class="logo" src="${esc(logo)}" alt="" />` : `<div class="logo"></div>`}
      <div class="school-text">
        <h1>${esc(setting?.name || "School Name")}</h1>
        ${
          address
            ? `<p>${esc(address)}${setting?.pin_code ? " - " + esc(setting.pin_code) : ""}</p>`
            : ""
        }
      </div>
      <div class="logo"></div>
    </div>

    <div class="rep-title">
      <span class="pill">CONSOLIDATED RESULT</span>
      <b>${esc(testTitle || "Progress Report")}</b>
    </div>

    <div class="meta" style="grid-template-columns: repeat(${metaCells.length}, 1fr)">
      ${metaCells
        .map(
          ([label, value]) => `<div><span>${label}</span><b>${value}</b></div>`,
        )
        .join("")}
    </div>

    <div class="tbl-wrap">
      <table>
        <thead>
          <tr>
            <th>Sr</th>
            <th class="l">Student</th>
            <th class="l">Father's Name</th>
            <th class="l">Mother's Name</th>
            <th>DOB</th>
            <th>Reg. No.</th>
            ${subjects
              .map(
                (sub) =>
                  `<th>${esc(sub?.name)}<small>Max ${esc(sub?.max ?? "-")}</small></th>`,
              )
              .join("")}
            <th>Total${sameTotal ? `<small>Max ${esc(maxTotal)}</small>` : ""}</th>
            ${sameTotal ? "" : "<th>Out of</th>"}
            <th>%</th>
            ${sh.rank ? "<th>Rank</th>" : ""}
          </tr>
        </thead>
        <tbody>
          ${body}
          ${summaryRow("Class Average", "avg", totalAvg, average)}
          ${summaryRow("Highest", "max", totalMax, highest)}
        </tbody>
      </table>
    </div>

    <div class="legend">
      AB = Absent &nbsp;|&nbsp; N/A = subject not applicable to the student
      &nbsp;|&nbsp; "-" = marks not entered
      &nbsp;|&nbsp; Average and highest are calculated for appeared students only.
    </div>

    ${
      signs.length
        ? `<div class="sign">
            ${signs
              .map(
                (s) => `<div class="${s.img ? "has-img" : ""}">
                  ${s.img ? `<img class="sig-img" src="${esc(s.img)}" alt="" />` : ""}
                  <i></i>${s.label}
                </div>`,
              )
              .join("")}
          </div>`
        : ""
    }
  </div>`;
};

/* ----------------------------------------------------------------- CSS */

export const getProgressTableCss = () => {
  const P = ".ptp";
  return `
  ${P}, ${P} * { box-sizing: border-box; }
  ${P} {
    --fs: 11px; --navy: #16233f; --line: #b8bfce; --soft: #e8ecf4;
    font-family: Arial, Helvetica, sans-serif; color: #111; background: #fff;
    font-size: var(--fs); width: 100%;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  ${P}.fs-lg { --fs: 12px; }
  ${P}.fs-md { --fs: 11px; }
  ${P}.fs-sm { --fs: 10px; }
  ${P}.fs-xs { --fs: 8.5px; }

  /* school header */
  ${P} .rep-head {
    display: grid; grid-template-columns: 18mm 1fr 18mm;
    align-items: center; gap: 10px; padding: 0 0 8px;
    border-bottom: 2px solid var(--navy);
  }
  ${P} .logo { width: 18mm; height: 18mm; object-fit: contain; }
  ${P} .school-text { text-align: center; min-width: 0; }
  ${P} .school-text h1 {
    margin: 0; font-size: calc(var(--fs) + 7px); line-height: 1.15;
    letter-spacing: .5px; text-transform: uppercase; color: var(--navy);
  }
  ${P} .school-text p { margin: 3px 0 0; font-size: calc(var(--fs) + 0px); color: #444; }

  ${P} .rep-title {
    display: flex; align-items: center; justify-content: center; gap: 10px;
    padding: 6px 10px; background: var(--navy); color: #fff;
    font-size: calc(var(--fs) + 2px);
  }
  ${P} .pill {
    border: 1px solid #fff; border-radius: 20px; padding: 1px 10px;
    font-size: calc(var(--fs) - 1px); font-weight: 700; letter-spacing: 1px; white-space: nowrap;
  }

  /* summary strip */
  ${P} .meta {
    display: grid;
    border: 1px solid var(--navy); border-top: 0; margin-bottom: 8px;
  }
  ${P} .meta > div { padding: 5px 8px; border-right: 1px solid var(--line); min-width: 0; }
  ${P} .meta > div:last-child { border-right: 0; }
  ${P} .meta span {
    display: block; font-size: calc(var(--fs) - 2.5px); color: #555;
    text-transform: uppercase; letter-spacing: .3px;
  }
  ${P} .meta b {
    display: block; font-size: calc(var(--fs) + 1px); color: var(--navy);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }

  /* table */
  ${P} .tbl-wrap { width: 100%; }
  ${P} table { width: 100%; border-collapse: collapse; border: 1.5px solid var(--navy); }
  ${P} thead { display: table-header-group; }
  ${P} tr { break-inside: avoid; page-break-inside: avoid; }
  ${P} th {
    background: var(--navy); color: #fff; font-weight: 700;
    padding: 6px 6px; text-align: center; vertical-align: middle;
    border: 1px solid #4a5878; white-space: nowrap;
  }
  ${P} th small {
    display: block; margin-top: 1px; font-size: calc(var(--fs) - 2px);
    font-weight: 400; opacity: .85;
  }
  ${P} td {
    padding: 5px 6px; text-align: center; vertical-align: middle;
    border: 1px solid var(--line); white-space: nowrap;
  }
  ${P} th.l, ${P} td.l { text-align: left; }
  ${P} td.name { font-weight: 700; white-space: normal; min-width: 30mm; }
  ${P} td.l { white-space: normal; }
  ${P} td.sr { color: #555; width: 1%; }
  ${P} td.tot { font-weight: 700; color: var(--navy); background: #f0f3f9; }
  ${P} td.dash { color: #888; }
  ${P} td.na {
    color: #a0a6b4; font-size: calc(var(--fs) - 2px); letter-spacing: .3px;
    background: #eceef3 !important;
  }
  ${P} tbody tr:nth-child(even) td { background: #f7f9fc; }
  ${P} tbody tr:nth-child(even) td.tot { background: #eaeef6; }

  ${P} .rk {
    display: inline-block; min-width: 1.9em; padding: 1px 6px;
    border-radius: 10px; font-weight: 700;
  }
  ${P} .rk.top.r1 { background: #f6d56b; color: #4a3a00; }
  ${P} .rk.top.r2 { background: #d8dde6; color: #2b3342; }
  ${P} .rk.top.r3 { background: #e8c3a0; color: #4a2c10; }

  ${P} tr.absent td, ${P} tr.pending td { color: #777; }
  ${P} tr.absent td.name, ${P} tr.pending td.name { color: #444; }
  ${P} .ab {
    display: inline-block; padding: 0 6px; border: 1px solid #c77; border-radius: 3px;
    color: #a33; font-size: calc(var(--fs) - 1px); font-weight: 700; letter-spacing: .5px;
  }

  ${P} tr.sum td {
    background: var(--soft) !important; font-weight: 700; color: var(--navy);
    border-top: 1px solid var(--navy);
  }
  ${P} tr.sum td.l { text-align: right; }
  ${P} tr.sum + tr.sum td { border-top: 1px solid var(--line); }

  ${P} .legend { margin-top: 6px; font-size: calc(var(--fs) - 2px); color: #555; }

  /* signatures — flex so 1, 2 or 3 of them share the width evenly */
  ${P} .sign {
    display: flex; gap: 40px;
    margin-top: 22mm; padding: 0 12mm; text-align: center;
    font-size: calc(var(--fs) - 0.5px); color: #333;
    break-inside: avoid; page-break-inside: avoid;
  }
  ${P} .sign > div { flex: 1; position: relative; }
  ${P} .sign i { display: block; border-top: 1px solid #111; margin-bottom: 3px; }
  ${P} .sign .sig-img {
    position: absolute; left: 50%; bottom: 100%;
    transform: translateX(-50%);
    max-width: 70%; max-height: 17mm;
    object-fit: contain; margin-bottom: 1px;
  }
  `;
};

/* --------------------------------------------------------------- PRINT */

export const printProgressTable = (opts) => {
  const { students = [], testTitle = "" } = opts || {};

  if (!students.length) {
    alert("Nothing to print!");
    return;
  }

  const win = window.open("", "_blank");
  if (!win) {
    alert("Popup blocked! Please allow popups to print.");
    return;
  }

  win.document.write(`<!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />
        <title>${esc(testTitle || "Progress Report")}</title>
        <style>
          @page {
            size: A4 landscape;
            margin: 8mm 8mm 12mm;
            @bottom-right {
              content: "Page " counter(page) " of " counter(pages);
              font: 9px Arial, sans-serif; color: #555;
            }
          }
          html, body { margin: 0; padding: 0; background: #fff; }
          ${getProgressTableCss()}
        </style>
      </head>
      <body>${buildProgressTableHtml(opts)}</body>
    </html>`);
  win.document.close();

  // wait for the logo / signature before printing
  const imgs = Array.from(win.document.images || []);
  Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) return resolve();
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        }),
    ),
  ).then(() => {
    setTimeout(() => {
      win.focus();
      win.print();
      win.close();
    }, 250);
  });
};
